import React from "react";
import Select from "react-select";
import { connect } from "react-redux";
import DateRangeFilter from "../DateRangePicker/DateRangeFilter";
import { useQuery } from "@tanstack/react-query";
import useCityOptions from "../../hooks/useCityOptions";
import useAdminCompanyOptions from "../../hooks/useAdminCompanyOptions";
import deriveCompanyScope from "../../utils/propertyHistoryCompanyScope";
import {
  companyPropertyOptionsQueryKey,
  companyRouteOptionsQueryKey,
} from "../../utils/companyFilterOptionQueryKeys";

const seasonOptions = [
  { value: "snow", label: "Snow" },
  { value: "landscape", label: "Landscape" },
];
const flaggedOptions = [{ value: true, label: "Yes" }];
const inputExtraStyles = {
  // borderColor: "#829399",
  borderColor: "rgb(204, 204, 204)",
};
export const PropertyHistoryFilters = (props) => {
  let filters = props.filters;
  if (filters.companies?.includes("all")) {
    // FIXME: should this be handled a level up?
    filters.companies = [];
  }

  const {
    companyOptions: ownCompanyOptions,
    companyIds: ownCompanyIds,
    isPending: ownCompaniesPending,
  } = useAdminCompanyOptions(props.currentUser);

  // The companies the viewer's companies have shared properties to. These can be
  // filtered on but not administered, so they are kept apart from the viewer's
  // own companies: only an own company can drive the dependent filters below.
  const servicePartnerQuery = useQuery({
    queryKey: ["company/me/service-partners"],
  });
  const ownCompanyIdSet = new Set(ownCompanyIds);
  const partnerCompanyOptions =
    servicePartnerQuery.data?.companies
      ?.filter((c) => !ownCompanyIdSet.has(c.id))
      ?.map((c) => ({ label: c.name, value: c.id })) ?? [];

  const selectedSingleCompany =
    filters.companies?.length === 1 ? filters.companies[0] : null;
  const cityQuery = useCityOptions(selectedSingleCompany);
  const cityOptions = cityQuery.cityOptions;
  const cityOptionsPending = cityQuery.isPending;

  const operatorOptionsQuery = useQuery({
    queryKey: [`company/${selectedSingleCompany}/users?take=1000`],
    enabled: selectedSingleCompany !== null,
  });
  const operatorOptions = operatorOptionsQuery.isPending
    ? []
    : operatorOptionsQuery.data.map((u) => {
        return { label: `${u.firstName} ${u.lastName}`, value: u.uid };
      });

  const routeNameOptionsQuery = useQuery({
    queryKey: companyRouteOptionsQueryKey(selectedSingleCompany),
    enabled: selectedSingleCompany !== null,
  });
  const routeNameOptions = routeNameOptionsQuery.isPending
    ? []
    : routeNameOptionsQuery.data.map((o) => {
        return { value: o._id, label: o.name };
      });

  const propertyOptionsQuery = useQuery({
    queryKey: companyPropertyOptionsQueryKey(selectedSingleCompany),
    enabled: selectedSingleCompany !== null,
  });
  const propertyOptions = propertyOptionsQuery.isPending
    ? []
    : propertyOptionsQuery.data.map((o) => {
        return { value: o._id, label: o.name };
      });

  const serviceOptionsQuery = useQuery({
    queryKey: [`company/${selectedSingleCompany}/services`],
    enabled: selectedSingleCompany !== null,
  });
  const serviceOptions = serviceOptionsQuery.isPending
    ? []
    : [
        {
          label: "Landscape Services",
          options: serviceOptionsQuery.data.landscape.map((option) => ({
            label: option.label,
            value: option.key,
          })),
        },
        {
          label: "Snow Services",
          options: serviceOptionsQuery.data.snow.map((option) => ({
            label: option.label,
            value: option.key,
          })),
        },
      ];

  const makeSetFilterFieldFnc = (field, multi = false) => {
    return async (value) => {
      setFilterValue(field, multi ? value.map((v) => v.value) : value?.value);
    };
  };

  const setFilterValue = (field, value) => {
    let newFilters = {
      ...filters,
      [field]: value,
    };
    props.onSubmitFilters(newFilters);
  };

  const getSelectedServiceFilterOptions = () => {
    if (filters.services?.length > 0) {
      let selected = [];
      let landscapeOptions = serviceOptions.find(
        (o) => o.label == "Landscape Services",
      );
      let snowOptions = serviceOptions.find((o) => o.label == "Snow Services");
      if (landscapeOptions) {
        selected.push(
          ...landscapeOptions.options.filter((o) =>
            filters.services.includes(o.value),
          ),
        );
      }
      if (snowOptions) {
        selected.push(
          ...snowOptions.options.filter((o) =>
            filters.services.includes(o.value),
          ),
        );
      }
      return selected;
    } else {
      return [];
    }
  };
  const allCompanyOptions = [...ownCompanyOptions, ...partnerCompanyOptions];
  // Offer nothing until we know which companies are the user's own, otherwise
  // the partner list is everything the service-partners endpoint returned and
  // the user's own companies would briefly be labelled as partners. Grouping
  // only earns its keep when there is actually something to tell apart.
  let companyOptions = [];
  if (ownCompanyOptions.length > 0) {
    companyOptions =
      partnerCompanyOptions.length > 0
        ? [
            { label: "My Companies", options: ownCompanyOptions },
            { label: "Service Partners", options: partnerCompanyOptions },
          ]
        : ownCompanyOptions;
  }

  return (
    <>
      <Select
        isMulti
        isClearable={true}
        options={companyOptions}
        placeholder="Filter by Servicing Company..."
        isLoading={ownCompaniesPending || servicePartnerQuery.isPending}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.servicedBy
            ? allCompanyOptions.filter((o) =>
                filters.servicedBy.includes(o.value),
              )
            : null
        }
        onChange={(o) => {
          let servicedBy = o.map((o) => o.value);
          let newFilters = {
            ...onlyFiltersThatWorkWithCompanyFilter(filters),
            servicedBy,
            companies: deriveCompanyScope({
              servicedBy,
              adminCompanyIds: ownCompanyIds,
              currentCompanyId: props.currentUser.currentCompanyId,
            }),
          };
          props.onSubmitFilters(newFilters);
        }}
      />
      <Select
        onChange={makeSetFilterFieldFnc("season")}
        options={seasonOptions}
        placeholder="Filter by Season"
        isClearable={true}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.season
            ? seasonOptions.find((o) => o.value == filters.season)
            : null
        }
      />
      <Select
        onChange={makeSetFilterFieldFnc("flaggedForReview")}
        options={flaggedOptions}
        placeholder="Filter by Flagged For Review"
        isClearable={true}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.flaggedForReview
            ? flaggedOptions.find((o) => o.value == filters.flaggedForReview)
            : null
        }
      />
      <Select
        isMulti
        onChange={makeSetFilterFieldFnc("cities", true)}
        isDisabled={selectedSingleCompany === null}
        isLoading={cityOptionsPending && selectedSingleCompany !== null}
        options={cityOptions}
        placeholder="Filter by City"
        isClearable={true}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.cities
            ? cityOptions.filter((o) => filters.cities.includes(o.value))
            : null
        }
      />
      <Select
        isMulti
        onChange={makeSetFilterFieldFnc("jobs", true)}
        options={propertyOptions}
        isDisabled={selectedSingleCompany === null}
        isLoading={
          propertyOptionsQuery.isPending && selectedSingleCompany !== null
        }
        placeholder="Filter by Property..."
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            // Set's the border color to match the Date Picker == 'snow-light-grey'
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.jobs
            ? propertyOptions.filter((o) => filters.jobs.includes(o.value))
            : null
        }
      />
      <Select
        isMulti
        onChange={makeSetFilterFieldFnc("routes", true)}
        options={routeNameOptions}
        placeholder="Filter by Route..."
        isDisabled={selectedSingleCompany === null}
        isLoading={
          routeNameOptionsQuery.isPending && selectedSingleCompany !== null
        }
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            // Set's the border color to match the Date Picker == 'snow-light-grey'
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.routes
            ? routeNameOptions.filter((o) => filters.routes.includes(o.value))
            : null
        }
      />
      <Select
        isMulti
        onChange={makeSetFilterFieldFnc("services", true)}
        options={serviceOptions}
        placeholder="Filter by Service..."
        isDisabled={selectedSingleCompany === null}
        isLoading={
          serviceOptionsQuery.isPending && selectedSingleCompany !== null
        }
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            // Set's the border color to match the Date Picker == 'snow-light-grey'
            ...inputExtraStyles,
          }),
        }}
        value={getSelectedServiceFilterOptions()}
      />
      <Select
        isMulti
        onChange={makeSetFilterFieldFnc("operators", true)}
        options={operatorOptions}
        placeholder="Filter by Operator..."
        isDisabled={selectedSingleCompany === null}
        isLoading={
          operatorOptionsQuery.isPending && selectedSingleCompany !== null
        }
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            // Set's the border color to match the Date Picker == 'snow-light-grey'
            ...inputExtraStyles,
          }),
        }}
        value={
          filters.operators
            ? operatorOptions.filter((o) => filters.operators.includes(o.value))
            : null
        }
      />
      <DateRangeFilter
        dateRange={
          filters.dateRange
            ? {
                startDate: filters.dateRange.firstDate,
                endDate: filters.dateRange.secondDate,
              }
            : null
        }
        waitForFullRange={true}
        buttonStyle={{
          width: "100%",
          ...inputExtraStyles,
          textAlign: "left",
        }}
        clearable={true}
        onChange={(v) => {
          if (v) {
            setFilterValue("dateRange", {
              firstDate: v.startDate,
              secondDate: v.endDate,
            });
          } else {
            setFilterValue("dateRange", null);
          }
        }}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(PropertyHistoryFilters);

const filtersThatWorkWithCompanyFilter = [
  "season",
  "flaggedForReview",
  "dateRange",
];
const onlyFiltersThatWorkWithCompanyFilter = (filters) => {
  let newObj = {};
  for (const filterOption of filtersThatWorkWithCompanyFilter) {
    if (filters[filterOption] !== undefined) {
      newObj[filterOption] = filters[filterOption];
    }
  }
  return newObj;
};
